import { lightTheme } from '@/customTheme'
import { styled, useStyletron } from 'styletron-react'
import Icons from '../Editor/components/Icons'
import Badge from '@/components/Badge'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { selectUser } from '@/store/slices/user/selectors'
import { useAppDispatch } from '@/store/store'
import { setOpenModalTryPremium } from '@/store/slices/user/actions'
import { customAmplitude } from '@/utils/customAmplitude'
import { SyncManager } from '@/firebase/SyncManager'

function Sidebar() {
  const history = useHistory()
  const { t } = useTranslation()
  const user = useSelector(selectUser)
  const dispatch = useAppDispatch()

  const handleReactive = () => {
    dispatch(setOpenModalTryPremium({
      isOpen: true,
      source: 'Comeback',
      callback: () => {
      }
    }))
    const eventProperties = {
      Source: 'Comeback',
      Type: 'Standard',
    }
    customAmplitude('Premium Prompt', eventProperties)
    // @ts-ignore
    window.dataLayer.push({ event: 'premium_prompt', ...eventProperties })    
  }

  const onHomeClick = () => {
    history.push('/create')
    customAmplitude('Click', { Source: 'sidebar.home' })
  }

  const onProjectsClick = () => {
    history.push('/create/projects')
    // @ts-ignore
    window.dataLayer.push({ event: 'header_projects_clicked' })
    customAmplitude('Click', { Source: 'sidebar.projects' })
  }

  const onBatchClick = () => {
    history.push('/batch-studio')
    // @ts-ignore
    window.dataLayer.push({ event: 'header_batch_clicked' })
    customAmplitude('Click', { Source: 'sidebar.batch' })
  }

  const onPhotoshootClick = () => {
    history.push('/create/photoshoots')
    // @ts-ignore
    window.dataLayer.push({ event: 'header_photoshoot_clicked' })
    customAmplitude('Click', { Source: 'sidebar.photoshoot' })
  }

  const onAIModelsClick = () => {
    history.push('/create/ai-models')
    // @ts-ignore
    window.dataLayer.push({ event: 'header_ai_models_clicked' })
    customAmplitude('Click', { Source: 'sidebar.ai_models' })
  }

  return (
    <div
      style={{
        height: '100%',
        width: '260px',
        padding: '16px',
        borderRight: '1px solid #e5e5e5',
        position: 'fixed',
        top: '64px',
        left: 0,
        zIndex: 1,
        backgroundColor: '#fff',
        cursor: SyncManager.shared.hasConnection ? 'pointer' : 'not-allowed'
      }}
    >
      <SidebarItem name="Home" icon={<Icons.HouseIcon />} handle={onHomeClick} path="/create" exact />
      <SidebarItem name="Projects" icon={<Icons.ProjectsIcon />} handle={onProjectsClick} path="/create/projects" />
      <SidebarSectionHeader label={t('AI tools')} />
      <SidebarItem name="Batch" icon={<Icons.BatchIcon />} handle={onBatchClick} path="/batch-studio" />
      <SidebarItem name="Photoshoot" icon={<Icons.PhotoshootIcon />} handle={onPhotoshootClick} path="/create/photoshoots" />
      <SidebarItem name="AI models" icon={<Icons.AIModels />} handle={onAIModelsClick} path="/create/ai-models" badge="New" />
      {user && !user.userIsPremium && user.userHadAFreeTrial ? <div style={{
        background: lightTheme.colors.grayScale50,
        borderRadius: '16px',
        bottom: '16px',
        left: '24px',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0,
        marginTop: '24px'
      }}>
        <p style={{...lightTheme.typography.Small14Semibold, color: lightTheme.colors.blackGray, textAlign: 'center'}}>{t('Come back to Bazaart Premium')}</p>
        <p style={{...lightTheme.typography.Small12regular, color: lightTheme.colors.blackGray, lineHeight: '20px', textAlign: 'center', marginTop: '4px'}}>{t('Get extra design superpowers!')}</p>
        <ButtonGetTheApp onClick={handleReactive}>{t('Reactivate now')}</ButtonGetTheApp>
      </div> : null}
    </div>
  )
}

export default Sidebar

function SidebarItem({ name, icon, handle, path, exact = false, badge = '' }) {
  const [css] = useStyletron()
  const { t } = useTranslation()
  const match = useRouteMatch({ path, exact })
  const active = !!match
  return (
    <div
      className={css({
        display: 'flex',
        padding: '8px',
        flexDirection: 'row',
        gap: '8px',
        alignItems: 'center',
        width: '100%',
        height: '48px',
        borderRadius: '8px',
        cursor: 'pointer',
        marginBottom: '8px',
        backgroundColor: active ? lightTheme.colors.grayScale100 : 'transparent',
        ':hover': {
            backgroundColor: lightTheme.colors.grayScale50,
        },
        ':active': {
            backgroundColor: lightTheme.colors.grayScale100,
        },
        opacity: SyncManager.shared.hasConnection ? 1 : 0.35,
        pointerEvents: SyncManager.shared.hasConnection ? 'auto' : 'none',
      })}
      onClick={handle}
    >
      {icon}
      <p style={{ ...lightTheme.typography.Small14regular, color: lightTheme.colors.blackGray }}>{t(name)}</p>
      {badge ? <Badge text={t(badge)} /> : null}
    </div>
  )
}

function SidebarSectionHeader({ label }: { label: string }) {
  return (
    <>
      <div style={{ borderBottom: `1px solid ${lightTheme.colors.grayScale100}`, width: '100%', margin: '4px 0 16px' }} />
      <p style={{ fontSize: '12px', fontWeight: '600', lineHeight: 'normal', color: lightTheme.colors.grayScale600, paddingLeft: '8px', marginBottom: '8px' }}>{label}</p>
    </>
  )
}

const ButtonGetTheApp = styled('div', (props: any) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: '8px 16px',
  height: '40px',
  width: '100%',
  borderRadius: '20px',
  cursor: 'pointer',
  ...lightTheme.typography.Small14Semibold,
  color: '#fff',
  transition: 'all 0.3s',
  marginTop: '16px',
  backgroundColor: lightTheme.colors.blackGray,
  ':hover': {
    backgroundColor: lightTheme.colors.grayScale800,
  },
  ':active': {
    backgroundColor: lightTheme.colors.grayScale700,
  },
}))
